import { SubmitHandler, useForm } from 'react-hook-form';
import { Input } from '../../../../ui/input/input';
import { Feedback } from '@repo/types/feedback';
import { usePostFeedback } from '../../api/use-post-feedback';
import { ErrorMessage } from '../error-message/error-message';
import { Button } from '../../../../ui/button/button';
import { Rating } from '../rating/rating';
import { Textarea } from '../../../../ui/textarea/textarea';
import { storedInteractions } from '../../utils/stored-interactions/stored-interactions';
import { Release } from '@repo/types/release';
import { useEffect } from 'react';
import { InfoMessage } from '../error-message/info-message';

type FeedbackFormValues = Omit<Feedback, 'releaseId' | 'id' | 'createdAt'>;

type FeedbackProps = {
  releaseId: number;
} & Pick<Release, 'catNr' | 'bandcampLink' | 'downloadLink'>;

export const FeedbackForm = ({
  releaseId,
  catNr,
  bandcampLink,
  downloadLink,
}: FeedbackProps) => {
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<FeedbackFormValues>();
  const { mutate, isSuccess, isLoading } = usePostFeedback(releaseId);

  const onSubmit: SubmitHandler<FeedbackFormValues> = (event) => {
    mutate(event);
  };

  useEffect(() => {
    if (isSuccess) {
      storedInteractions.setHasLeftFeedback(catNr);
    }
  }, [isSuccess]);

  const hasLeftFeedback =
    isSuccess || storedInteractions.getHasLeftFeedback(catNr);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="min-h-[350px]">
      <div className="flex flex-col gap-4 mb-4 h-full relative">
        <div className="flex flex-col">
          <Input
            type="text"
            aria-label="Artist name input field"
            placeholder="Name"
            {...register('artistName', { required: 'Name is required' })}
          />
          {errors.artistName && (
            <ErrorMessage text={`${errors.artistName.message}`} />
          )}
        </div>

        <div>
          <Rating
            amount={5}
            selected={undefined}
            setValue={setValue}
            register={register('rating', { required: 'Rating is required' })}
          />
          {errors.rating && <ErrorMessage text={`${errors.rating.message}`} />}
        </div>

        <div className="flex flex-col">
          <Textarea
            aria-label="Feedback input field"
            className="flex-1"
            placeholder="Feedback"
            {...register('content', { required: 'Feedback is required' })}
          />
          {errors.content && (
            <ErrorMessage text={`${errors.content.message}`} />
          )}
        </div>

        {hasLeftFeedback && (
          <div className="bg-default h-full w-full absolute bg-opacity-90 flex items-center justify-center">
            <Button
              type="button"
              onClick={() => {
                window.open(downloadLink, '_blank', 'noopener,noreferrer');
              }}
            >
              DOWNLOAD
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <InfoMessage text="Loading..." />
      ) : (
        <div className="flex items-center justify-between">
          <a
            className="underline hover:no-underline text-2xl"
            href={bandcampLink}
            target="_blank"
          >
            BUY / PREORDER
          </a>
          {!hasLeftFeedback && <Button>SUBMIT</Button>}
        </div>
      )}
    </form>
  );
};
